import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  Divider,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Phone,
  Email,
  LocationOn,
  Business,
  LocalHospital,
  Assignment,
  Comment,
  AttachFile,
  CloudUpload,
  Download,
  Delete,
  Visibility,
  ExpandMore,
  Schedule,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { fetchLeadById, updateLead, addLeadComment } from '../../store/slices/leadsSlice';
import { leadsService } from '../../services/leads';
import { LEAD_STATUS, LEAD_STATUS_LABELS, LEAD_PRIORITY, LEAD_PRIORITY_LABELS, DOCUMENT_TYPE_LABELS } from '../../services/leads';
import DocumentUpload from './DocumentUpload';
import BulkDocumentUpload from './BulkDocumentUpload';
import LeadReassignDialog from './LeadReassignDialog';
import DocumentViewer from './DocumentViewer';
import FacebookCampaignInfo from './FacebookCampaignInfo';
import AssignmentHistory from './AssignmentHistory';
import StatusHistory from './StatusHistory';
import LeadMetrics from './LeadMetrics';
import LoadingSpinner from '../common/LoadingSpinner';

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLead, loading } = useSelector((state) => state.leads);
  const { user } = useSelector((state) => state.auth);

  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [documentUploadOpen, setDocumentUploadOpen] = useState(false);
  const [bulkDocumentUploadOpen, setBulkDocumentUploadOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [comments, setComments] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (id) {
      dispatch(fetchLeadById(id));
      fetchLeadDocuments();
      fetchLeadComments();
      fetchAssignableUsers();
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentLead) {
      setEditData({
        status: currentLead.status,
        priority: currentLead.priority,
        notes: currentLead.notes || '',
        follow_up_date: currentLead.follow_up_date ? currentLead.follow_up_date.split('T')[0] : '',
      });
    }
  }, [currentLead]);

  const fetchLeadDocuments = async () => {
    try {
      const docs = await leadsService.getLeadDocuments(id);
      // Service method now guarantees an array response
      if (!Array.isArray(docs)) {
        console.error('LeadDetails: getLeadDocuments returned non-array:', docs, typeof docs);
        setDocuments([]);
      } else {
        setDocuments(docs);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments([]);
    }
  };

  const fetchLeadComments = async () => {
    try {
      const cmts = await leadsService.getLeadComments(id);
      setComments(cmts.results || cmts);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const users = await leadsService.getAssignableUsers(id);
      setAssignableUsers(users);
    } catch (error) {
      console.error('Failed to fetch assignable users:', error);
    }
  };

  const handleEditSubmit = async () => {
    try {
      console.log('LeadDetails: Edit data:', editData);
      console.log('LeadDetails: Current lead:', currentLead);
      
      // Validate that currentLead has required fields
      if (!currentLead.name || !currentLead.phone || !currentLead.city || !currentLead.disease) {
        console.error('LeadDetails: Current lead is missing required fields:', {
          name: !currentLead.name,
          phone: !currentLead.phone,
          city: !currentLead.city,
          disease: !currentLead.disease
        });
        throw new Error('Lead is missing required fields. Please refresh the page.');
      }
      
      // Merge edit data with complete lead data to ensure all required fields are present
      const completeLeadData = {
        // Start with existing lead data
        ...currentLead,
        // Override with edit changes
        ...editData,
        // Ensure required fields are always present
        name: currentLead.name,
        phone: currentLead.phone,
        state: currentLead.state?.id || currentLead.state_id || currentLead.state,
        disease: currentLead.disease?.id || currentLead.disease_id || currentLead.disease,
        // Handle the lead_source field mapping
        lead_source: currentLead.lead_source || currentLead.source
      };
      
      console.log('LeadDetails: Complete data being sent:', completeLeadData);
      
      await dispatch(updateLead({ id, leadData: completeLeadData })).unwrap();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update lead:', error);
    }
  };

  const handleCommentSubmit = async () => {
    try {
      const commentData = {
        comment: newComment,
        is_internal: isInternal,
      };
      await dispatch(addLeadComment({ leadId: id, comment: commentData })).unwrap();
      setNewComment('');
      setCommentDialogOpen(false);
      fetchLeadComments(); // Refresh comments
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleDocumentUpload = async (leadId, file, documentType, description) => {
    try {
      const response = await leadsService.uploadLeadDocument(leadId, file, documentType, description);
      fetchLeadDocuments(); // Refresh documents
      return response;
    } catch (error) {
      console.error('Failed to upload document:', error);
      throw error;
    }
  };

  const handleBulkDocumentUpload = async (leadId, files, documentTypes, descriptions) => {
    try {
      const response = await leadsService.bulkUploadDocuments(leadId, files, documentTypes, descriptions);
      fetchLeadDocuments(); // Refresh documents
      return response;
    } catch (error) {
      console.error('Failed to bulk upload documents:', error);
      throw error;
    }
  };

  const handleDocumentDownload = async (documentId) => {
    try {
      // Always use the secure download endpoint for security
      const downloadData = await leadsService.downloadDocument(documentId);

      // Open the presigned URL for download
      if (downloadData.download_url) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = downloadData.download_url;
        link.download = downloadData.filename || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      return downloadData;
    } catch (error) {
      console.error('Failed to download document:', error);
      throw error;
    }
  };

  const handleDocumentDelete = async (documentId) => {
    try {
      await leadsService.deleteDocument(documentId);
      fetchLeadDocuments(); // Refresh documents
    } catch (error) {
      console.error('Failed to delete document:', error);
      throw error;
    }
  };

  const handleBulkDocumentDelete = async (documentIds) => {
    try {
      const response = await leadsService.bulkDeleteDocuments(documentIds);
      console.log('Bulk delete response:', response);
      fetchLeadDocuments(); // Refresh documents
      return response;
    } catch (error) {
      console.error('Failed to bulk delete documents:', error);
      throw error;
    }
  };

  const handleReassignLead = async (leadId, reassignData) => {
    try {
      await leadsService.reassignLead(leadId, reassignData);
      dispatch(fetchLeadById(id)); // Refresh lead data
      fetchAssignableUsers(); // Refresh assignable users
    } catch (error) {
      console.error('Failed to reassign lead:', error);
      throw error;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'primary',
      'warm': 'info',
      'hot': 'warning',
      'followup': 'warning',
      'ipd_done': 'success',
      'opd_done': 'success',
      'converted': 'success',
      'dnp': 'error',
      'dead': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'error',
      'medium': 'warning',
      'low': 'success'
    };
    return colors[priority] || 'default';
  };

  const isFollowupOverdue = (followupDate) => {
    if (!followupDate) return false;
    const today = new Date();
    const followup = new Date(followupDate);
    return followup < today;
  };

  if (loading) {
    return <LoadingSpinner message="Loading lead details..." />;
  }

  if (!currentLead) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6">Lead not found</Typography>
        <Button onClick={() => navigate('/leads')} startIcon={<ArrowBack />}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  const renderLeadInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 56, height: 56 }}>
                  {currentLead.name?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h5">{currentLead.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={LEAD_STATUS_LABELS[currentLead.status]}
                      color={getStatusColor(currentLead.status)}
                      size="small"
                    />
                    <Chip
                      label={LEAD_PRIORITY_LABELS[currentLead.priority]}
                      color={getPriorityColor(currentLead.priority)}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setEditDialogOpen(true)}
              >
                Edit
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Phone color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{currentLead.phone}</Typography>
                  </Box>
                </Box>
              </Grid>

              {currentLead.alternate_phone && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Phone color="secondary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Alternate Phone</Typography>
                      <Typography variant="body1">{currentLead.alternate_phone}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {currentLead.email && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Email color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{currentLead.email}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOn color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Typography variant="body1">
                      {currentLead.city_name}
                      {currentLead.state_name && `, ${currentLead.state_name}`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalHospital color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Disease</Typography>
                    <Typography variant="body1">{currentLead.disease_name}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Assignment color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Assigned To</Typography>
                    <Typography variant="body1">
                      {currentLead.assigned_to_full_name || currentLead.assigned_to_name}
                    </Typography>
                    {currentLead.assigned_to_role && (
                      <Chip
                        label={currentLead.assigned_to_role.toUpperCase()}
                        size="small"
                        color={
                          currentLead.assigned_to_role === 'admin' ? 'error' :
                          currentLead.assigned_to_role === 'manager' ? 'warning' :
                          currentLead.assigned_to_role === 'team_lead' ? 'info' : 'success'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              {currentLead.follow_up_date && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {isFollowupOverdue(currentLead.follow_up_date) ? (
                      <Warning color="error" />
                    ) : (
                      <Schedule color="primary" />
                    )}
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Follow-up Date
                        {isFollowupOverdue(currentLead.follow_up_date) && (
                          <Chip 
                            label="OVERDUE" 
                            color="error" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(currentLead.follow_up_date).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Address</Typography>
                <Typography variant="body1">{currentLead.address}</Typography>
              </Grid>

              {currentLead.symptoms && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Symptoms</Typography>
                  <Typography variant="body1">{currentLead.symptoms}</Typography>
                </Grid>
              )}

              {currentLead.medical_history && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Medical History</Typography>
                  <Typography variant="body1">{currentLead.medical_history}</Typography>
                </Grid>
              )}

              {currentLead.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{currentLead.notes}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Phone />}
                href={`tel:${currentLead.phone}`}
                fullWidth
              >
                Call Primary
              </Button>
              {currentLead.alternate_phone && (
                <Button
                  variant="outlined"
                  startIcon={<Phone />}
                  href={`tel:${currentLead.alternate_phone}`}
                  fullWidth
                >
                  Call Alternate
                </Button>
              )}
              {currentLead.email && (
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  href={`mailto:${currentLead.email}`}
                  fullWidth
                >
                  Send Email
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<Comment />}
                onClick={() => setCommentDialogOpen(true)}
                fullWidth
              >
                Add Comment
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => setDocumentUploadOpen(true)}
                fullWidth
              >
                Upload Document
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => setBulkDocumentUploadOpen(true)}
                fullWidth
              >
                Bulk Upload Documents
              </Button>
              <Button
                variant="outlined"
                startIcon={<Assignment />}
                onClick={() => setReassignDialogOpen(true)}
                fullWidth
              >
                Reassign Lead
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Timeline/History */}
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Key Information</Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(currentLead.created_at).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Updated: {new Date(currentLead.updated_at).toLocaleString()}
              </Typography>
              {currentLead.assigned_at && (
                <Typography variant="body2" color="text.secondary">
                  Assigned: {new Date(currentLead.assigned_at).toLocaleString()}
                </Typography>
              )}
              {currentLead.last_contacted && (
                <Typography variant="body2" color="text.secondary">
                  Last Contacted: {new Date(currentLead.last_contacted).toLocaleString()}
                </Typography>
              )}
              {currentLead.days_since_created && (
                <Typography variant="body2" color="text.secondary">
                  Age: {currentLead.days_since_created} days
                </Typography>
              )}
            </Box>

            {/* Quick Stats */}
            {(currentLead.total_comments > 0 || currentLead.total_documents > 0) && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>Quick Stats</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Chip
                      label={`${currentLead.total_comments || 0} Comments`}
                      size="small"
                      icon={<Comment />}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Chip
                      label={`${currentLead.total_documents || 0} Documents`}
                      size="small"
                      icon={<AttachFile />}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderComments = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Comments ({Array.isArray(comments) ? comments.length : 0})</Typography>
          <Button
            variant="outlined"
            startIcon={<Comment />}
            onClick={() => setCommentDialogOpen(true)}
          >
            Add Comment
          </Button>
        </Box>

        <List>
          {Array.isArray(comments) && comments.map((comment) => (
            <ListItem key={comment.id} alignItems="flex-start" divider>
              <ListItemIcon>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {comment.user_name?.charAt(0)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {comment.user_name}
                    </Typography>
                    <Chip
                      label={comment.is_internal ? 'Internal' : 'Customer'}
                      size="small"
                      color={comment.is_internal ? 'primary' : 'default'}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(comment.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {comment.comment}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        {(!Array.isArray(comments) || comments.length === 0) && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Comment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No comments yet. Add the first comment to start the conversation.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderDocuments = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Documents ({Array.isArray(documents) ? documents.length : 0})</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => setDocumentUploadOpen(true)}
              size="small"
            >
              Upload Single
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setBulkDocumentUploadOpen(true)}
              size="small"
            >
              Bulk Upload
            </Button>
          </Box>
        </Box>

        <DocumentViewer
          documents={documents}
          onDownload={handleDocumentDownload}
          onDelete={handleDocumentDelete}
          onBulkDelete={handleBulkDocumentDelete}
          leadId={id}
          loading={loading}
        />
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/leads')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">Lead Details</Typography>
      </Box>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Metrics" />
        <Tab label={`Comments (${Array.isArray(comments) ? comments.length : 0})`} />
        <Tab label={`Documents (${Array.isArray(documents) ? documents.length : 0})`} />
        <Tab label="History" />
        <Tab label="Campaign" />
      </Tabs>

      {tabValue === 0 && renderLeadInfo()}
      {tabValue === 1 && <LeadMetrics lead={currentLead} />}
      {tabValue === 2 && renderComments()}
      {tabValue === 3 && renderDocuments()}
      {tabValue === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <AssignmentHistory
              assignmentHistory={currentLead?.assignment_history}
              currentLead={currentLead}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StatusHistory
              statusHistory={currentLead?.status_history}
              currentStatus={currentLead?.status}
            />
          </Grid>
        </Grid>
      )}
      {tabValue === 5 && <FacebookCampaignInfo lead={currentLead} />}

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Lead</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editData.status || ''}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  label="Status"
                >
                  {Object.entries(LEAD_STATUS_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={editData.priority || ''}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                  label="Priority"
                >
                  {Object.entries(LEAD_PRIORITY_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Follow-up Date"
                type="datetime-local"
                value={editData.follow_up_date || ''}
                onChange={(e) => setEditData({ ...editData, follow_up_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Add Comment Dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Comment Type</InputLabel>
            <Select
              value={isInternal}
              onChange={(e) => setIsInternal(e.target.value)}
              label="Comment Type"
            >
              <MenuItem value={true}>Internal (Team Only)</MenuItem>
              <MenuItem value={false}>Customer Visible</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCommentSubmit}>Add Comment</Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <DocumentUpload
        open={documentUploadOpen}
        onClose={() => setDocumentUploadOpen(false)}
        onUpload={handleDocumentUpload}
        leadId={id}
        existingDocuments={documents}
      />

      {/* Bulk Document Upload Dialog */}
      <BulkDocumentUpload
        open={bulkDocumentUploadOpen}
        onClose={() => setBulkDocumentUploadOpen(false)}
        onUpload={handleBulkDocumentUpload}
        leadId={id}
        existingDocuments={documents}
      />

      {/* Lead Reassignment Dialog */}
      <LeadReassignDialog
        open={reassignDialogOpen}
        onClose={() => setReassignDialogOpen(false)}
        onReassign={handleReassignLead}
        lead={currentLead}
        assignableUsers={assignableUsers}
        loading={loading}
      />
    </Box>
  );
};

export default LeadDetails;